import { useEffect, useState } from 'react';
import { LineConfig, TransformedDataPoint } from '../helpers/prepareChartData';
import { getClosedLeadEngagements } from '../../../../services/api';
import { prepareLeadsChartData } from '../helpers/prepareLeadsChartData';
import { subMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { prepareCloseReasonChartData } from '../helpers/prepareCloseReasonChartData';

export const useCloseWithReason = () => {
  const { t } = useTranslation();
  const [from, setFrom] = useState('2026-04-01');
  const [to, setTo] = useState('2026-04-20');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [chartsDataLeads, setChartsDataLeads] = useState<TransformedDataPoint[] | null>(null);
  const [chartsLinesLeads, setChartsLinesLeads] = useState<LineConfig[] | null>(null);
  const [chartsDataEngagements, setChartsDataEngagements] = useState<TransformedDataPoint[] | null>(
    null,
  );
  const [chartsLinesEngagements, setChartsLinesEngagements] = useState<LineConfig[] | null>(null);

  const fetchData = async (from: string, to: string) => {
    console.log('Fetching data with from:', from, 'to:', to);
    setLoading(true);
    setError(null);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const rawData = await getClosedLeadEngagements({ from, to, timezone });
      console.log('Fetched useCloseWithReason charts data:', rawData);

      let leadsData: TransformedDataPoint[];
      let leadsLines: LineConfig[];
      // If leads is an object keyed by dates, transform it; otherwise assume it's already an array for prepareLeadsChartData
      if (rawData.leads && !Array.isArray(rawData.leads)) {
        const transformed = prepareCloseReasonChartData(rawData.leads);
        leadsData = transformed.data;
        leadsLines = transformed.lines;
        const localizedLines = leadsLines.map((l) => ({
          ...l,
          name: t(`global.closeReason.${l.name}`, { defaultValue: l.name }),
        }));
        leadsLines = localizedLines;
      } else {
        const res = prepareLeadsChartData(rawData.leads as any);
        leadsData = res.data;
        leadsLines = res.lines as any;
      }

      let engagementsData: TransformedDataPoint[];
      let engagementsLines: LineConfig[];
      if (rawData.engagements && !Array.isArray(rawData.engagements)) {
        const transformed = prepareCloseReasonChartData(rawData.engagements);
        engagementsData = transformed.data;
        engagementsLines = transformed.lines;
        const localizedLines = engagementsLines.map((l) => ({
          ...l,
          name: t(`global.closeReason.${l.name}`, { defaultValue: l.name }),
        }));
        engagementsLines = localizedLines;
      } else {
        const res = prepareLeadsChartData(rawData.engagements as any);
        engagementsData = res.data;
        engagementsLines = res.lines as any;
      }

      setChartsDataLeads(leadsData);
      setChartsLinesLeads(leadsLines);
      setChartsDataEngagements(engagementsData);
      setChartsLinesEngagements(engagementsLines);
    } catch (err) {
      console.error('Error fetching leads charts data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setTo(new Date().toISOString().split('T')[0]); // Set default to date to today
    const monthAgo = subMonths(new Date(), 1).toISOString().split('T')[0];
    setFrom(monthAgo);
    fetchData(monthAgo, new Date().toISOString().split('T')[0]);
  }, []);

  return {
    from,
    setFrom,
    to,
    setTo,
    error,
    setError,
    chartsDataLeads,
    loading,
    chartsLinesLeads,
    chartsDataEngagements,
    chartsLinesEngagements,
    fetchData,
  };
};
